import i18n from '../config/config';
import { LOCALES, LocaleType, DEFAULT_LANGUAGE, DEFAULT_NAMESPACE, HOST_LOCALE_MAPPING } from '../config/constants';
import { TRANSLATION_RESOURCES } from '../config/resources';
import type { TOptions, Resource } from 'i18next';

export const translate = (slug: string, options?: TOptions) => {
  const t = i18n.getFixedT(i18n.language, options?.ns ?? DEFAULT_NAMESPACE);

  return t(slug, options);
};

export const initResources = async (lang: string, namespaces: string[], i18nInstance = i18n): Promise<void> => {
  if (Array.isArray(namespaces)) {
    for (const namespace of namespaces) {
      const translations = await import(`../locales/${lang}/${namespace}.json`);
      await i18nInstance.addResourceBundle(lang, namespace, translations.default, true, true);
    }
  }
};

export const setLanguage = async (lang: string, namespaces?: string[], i18nInstance = i18n) => {
  if (Array.isArray(namespaces)) {
    for (const namespace of namespaces) {
      const translations = await import(`../locales/${lang}/${namespace}.json`);
      await i18nInstance.addResourceBundle(lang, namespace, translations.default, true, true);
    }
  } else {
    const translations = await import(`../locales/${lang}/common.json`);
    await i18nInstance.addResourceBundle(lang, 'common', translations.default, true, true);
  }

  await i18nInstance.changeLanguage(lang);
  return i18nInstance.getDataByLanguage(lang);
};

export const isTranslationAvailable = (translationCode: string): boolean => {
  return !!TRANSLATION_RESOURCES[translationCode];
};

export const getLanguageCodeByHost = (host: string): LocaleType => {
  const subdomain = host?.split('.')?.[0];

  if (subdomain === 'www' || subdomain === 'benzinga') {
    return DEFAULT_LANGUAGE;
  }

  if (isTranslationAvailable(HOST_LOCALE_MAPPING[subdomain])) {
    return HOST_LOCALE_MAPPING[subdomain];
  }

  return DEFAULT_LANGUAGE;
};

export const setLanguageByHost = async (host: string, namespaces?: string[], i18nInstance = i18n) => {
  const translationCode = getLanguageCodeByHost(host);

  if (translationCode) {
    return await setLanguage(translationCode, namespaces, i18nInstance);
  }

  return null;
};

export const getResources = (language: string, targetResources?: Record<string, unknown> | null): Resource => {
  const resources = { ...TRANSLATION_RESOURCES };

  if (language !== LOCALES.EN && targetResources) {
    resources[language] = targetResources;
  } else if (language === LOCALES.EN) {
    resources[LOCALES.EN] = {
      ...resources[LOCALES.EN],
      ...targetResources,
    };
  }

  return resources;
};
